import { useState, useCallback } from 'react';
import { getStates, getLgasByState, getLcdasByLga } from '../services/api';

export const useLocationData = () => {
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [lcdas, setLcdas] = useState([]);
  const [showLcdaField, setShowLcdaField] = useState(false);
  
  // Loading states
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingLgas, setLoadingLgas] = useState(false);
  const [loadingLcdas, setLoadingLcdas] = useState(false);

  const loadLocationData = useCallback(async (user, updateForm) => {
    try {
      setLoadingStates(true);
      const statesResponse = await getStates();
      const statesData = statesResponse.data || [];
      
      setStates(statesData.map(state => ({ 
        ...state,
        label: state.name,
        value: String(state.state_id)
      })));

      // Pre-populate user location if available
      if (user?.state_id) {
        updateForm({ state_id: String(user.state_id) });
        await fetchLgas(user.state_id);
        
        if (user?.lga_id) {
          updateForm({ lga_id: String(user.lga_id) });
          await fetchLcdas(user.lga_id);
          
          if (user?.lcda_id && lcdas.length > 0) {
            updateForm({ lcda_id: String(user.lcda_id) });
          }
        }
      }
    } catch (err) {
      // console.error('❌ Error loading location data:', err);
      throw err;
    } finally {
      setLoadingStates(false);
    }
  }, []);

  const fetchLgas = useCallback(async (stateId) => {
    if (!stateId) {
      setLgas([]);
      setLcdas([]);
      setShowLcdaField(false);
      return;
    }

    try {
      setLoadingLgas(true);
      const response = await getLgasByState(stateId);
      const lgasData = response.data || [];
      setLgas(lgasData.map(lga => ({
        ...lga,
        label: lga.name,
        value: String(lga.lga_id)
      })));
      setLcdas([]);
      setShowLcdaField(false);
    } catch (err) {
      // console.error('❌ Error fetching LGAs:', err);
      throw err;
    } finally {
      setLoadingLgas(false);
    }
  }, []);

  const fetchLcdas = useCallback(async (lgaId) => {
    if (!lgaId) {
      setLcdas([]);
      setShowLcdaField(false);
      return []; // Return empty array
    }
  
    try {
      setLoadingLcdas(true);
      const response = await getLcdasByLga(lgaId);
      const lcdasData = response.data || [];
      
      setLcdas(lcdasData.map(lcda => ({
        ...lcda,
        label: lcda.name,
        value: String(lcda.lcda_id)
      })));
      
      setShowLcdaField(lcdasData.length > 0);
      return lcdasData; // Return the data
    } catch (err) {
      // console.error('❌ Error fetching LCDAs:', err);
      setLcdas([]);
      setShowLcdaField(false);
      return []; // Return empty array on error
    } finally {
      setLoadingLcdas(false);
    }
  }, []);

  return {
    states,
    lgas,
    lcdas,
    showLcdaField,
    loadingStates,
    loadingLgas,
    loadingLcdas,
    loadLocationData,
    fetchLgas,
    fetchLcdas
  };
};